import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';

import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';
import { EventFormDialog } from './EventFormDialog';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const EVENTS_API = properties.baseURL + properties.adminEventsPath;

const emptyEvent = {
    id: null,
    name: '',
    location: '',
    city: '',
    state: '',
    startDate: '',
    endDate: '',
    newsUrl: '',
    instructors: '',
    details: '',
    status: 'DRAFT',
    activities: [],
};

async function fetchEvents() {
    const res = await apiFetch(EVENTS_API);
    if (!res.ok) throw new Error(`Erro ${res.status}`);
    return res.json();
}

async function postEvent(body) {
    const res = await apiFetch(EVENTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
    }
    return res.json();
}

async function putEvent(id, body) {
    const res = await apiFetch(`${EVENTS_API}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
    }
    return res.json();
}

async function patchStatus(id, status) {
    const res = await apiFetch(`${EVENTS_API}/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
    }
    return res.json();
}

async function uploadPoster(id, file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiFetch(`${EVENTS_API}/${id}/poster`, {
        method: 'POST',
        body: formData,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
    }
    return res.json();
}

async function doDeleteEvent(id) {
    const res = await apiFetch(`${EVENTS_API}/${id}`, { method: 'DELETE' });
    if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Existem inscrições neste evento');
    }
    if (!res.ok && res.status !== 204) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erro ${res.status}`);
    }
}

function EventList() {
    const [events, setEvents] = useState([]);
    const [event, setEvent] = useState(emptyEvent);
    const [eventDialog, setEventDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [posterFile, setPosterFile] = useState(null);
    const [apiError, setApiError] = useState('');
    const toast = useRef(null);
    const dt = useRef(null);

    useEffect(() => { loadEvents(); }, []);

    const loadEvents = async () => {
        try {
            const data = await fetchEvents();
            setEvents(data);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 4000 });
        }
    };

    const openNew = () => {
        setEvent({ ...emptyEvent });
        setPosterFile(null);
        setSubmitted(false);
        setActiveIndex(0);
        setApiError('');
        setEventDialog(true);
    };

    const openEdit = (row) => {
        setEvent({ ...row, activities: row.activities || [] });
        setPosterFile(null);
        setSubmitted(false);
        setActiveIndex(0);
        setApiError('');
        setEventDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setApiError('');
        setEventDialog(false);
    };

    const onChange = (field, value) => {
        setEvent(prev => ({ ...prev, [field]: value }));
    };

    const onActivitiesChange = (activities) => {
        setEvent(prev => ({ ...prev, activities }));
    };

    const saveEvent = async () => {
        setSubmitted(true);
        if (!event.name?.trim()) return;

        setApiError('');
        try {
            let saved;
            if (event.id) {
                saved = await putEvent(event.id, event);
                setEvents(prev => prev.map(e => e.id === saved.id ? saved : e));
                toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Evento atualizado', life: 3000 });
            } else {
                saved = await postEvent(event);
                setEvents(prev => [...prev, saved]);
                toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Evento criado', life: 3000 });
            }

            if (posterFile) {
                try {
                    const updated = await uploadPoster(saved.id, posterFile);
                    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
                    toast.current.show({ severity: 'info', summary: 'Cartaz', detail: 'Cartaz enviado com sucesso', life: 3000 });
                } catch (posterErr) {
                    toast.current.show({
                        severity: 'warn', summary: 'Aviso',
                        detail: `Evento salvo, mas erro no cartaz: ${posterErr.message}`, life: 5000,
                    });
                }
            }

            setEventDialog(false);
            setEvent(emptyEvent);
        } catch (err) {
            setApiError(err.message);
        }
    };

    const toggleStatus = async (row) => {
        const newStatus = row.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
        try {
            const updated = await patchStatus(row.id, newStatus);
            setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
            toast.current.show({
                severity: 'success', summary: 'Sucesso',
                detail: newStatus === 'PUBLISHED' ? 'Evento publicado' : 'Evento despublicado',
                life: 3000,
            });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 4000 });
        }
    };

    const confirmDelete = (row) => {
        setDeleteTarget(row);
        setDeleteDialog(true);
    };

    const executeDelete = async () => {
        try {
            await doDeleteEvent(deleteTarget.id);
            setEvents(prev => prev.filter(e => e.id !== deleteTarget.id));
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Evento excluído', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        } finally {
            setDeleteDialog(false);
            setDeleteTarget(null);
        }
    };

    // ==================== BODY TEMPLATES ====================

    const statusBodyTemplate = (row) => (
        <Tag
            value={row.status === 'PUBLISHED' ? 'Publicado' : 'Rascunho'}
            severity={row.status === 'PUBLISHED' ? 'success' : 'warning'}
        />
    );

    const activitiesBodyTemplate = (row) => {
        const count = (row.activities || []).length;
        return <span>{count} atividade{count !== 1 ? 's' : ''}</span>;
    };

    const actionBodyTemplate = (row) => (
        <>
            <Button icon="pi pi-pencil"
                className="p-button-rounded p-button-success mr-2"
                onClick={() => openEdit(row)} tooltip="Editar"
                tooltipOptions={{ position: 'bottom' }} />
            <Button
                icon={row.status === 'PUBLISHED' ? 'pi pi-eye-slash' : 'pi pi-eye'}
                className={`p-button-rounded mr-2 ${row.status === 'PUBLISHED' ? 'p-button-warning' : 'p-button-info'}`}
                onClick={() => toggleStatus(row)}
                tooltip={row.status === 'PUBLISHED' ? 'Despublicar' : 'Publicar'}
                tooltipOptions={{ position: 'bottom' }} />
            <Button icon="pi pi-list"
                className="p-button-rounded p-button-secondary mr-2"
                onClick={() => { window.location.href = `/events/${row.id}/registrations`; }}
                tooltip="Inscrições"
                tooltipOptions={{ position: 'bottom' }} />
            <Button icon="pi pi-trash"
                className="p-button-rounded p-button-danger"
                onClick={() => confirmDelete(row)} tooltip="Excluir"
                tooltipOptions={{ position: 'bottom' }} />
        </>
    );

    // ==================== HEADER ====================

    const header = (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between">
            <span className="p-input-icon-left w-full md:w-auto">
                <i className="pi pi-search" />
                <InputText type="search"
                    onInput={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Buscar..." className="w-full lg:w-auto" />
            </span>
            <div className="mt-3 md:mt-0 flex justify-content-end">
                <Button icon="pi pi-plus" className="mr-2 p-button-rounded"
                    onClick={openNew} tooltip="Novo Evento"
                    tooltipOptions={{ position: 'bottom' }} />
                <Button icon="pi pi-upload" className="p-button-help p-button-rounded"
                    onClick={() => dt.current.exportCSV()} tooltip="Exportar"
                    tooltipOptions={{ position: 'bottom' }} />
            </div>
        </div>
    );

    const deleteFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text"
                onClick={() => setDeleteDialog(false)} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text"
                onClick={executeDelete} />
        </>
    );

    // ==================== RENDER ====================

    return (
        <div className="datatable-crud-demo surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />
            <div className="text-3xl text-800 font-bold mb-4">Eventos</div>

            <DataTable ref={dt} value={events}
                dataKey="id" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} eventos"
                globalFilter={globalFilter} header={header} responsiveLayout="scroll">
                <Column field="id" header="ID" sortable style={{ minWidth: '5rem' }} />
                <Column field="name" header="Nome" sortable style={{ minWidth: '16rem' }} />
                <Column field="city" header="Cidade" sortable style={{ minWidth: '10rem' }} />
                <Column field="state" header="UF" sortable style={{ minWidth: '4rem' }} />
                <Column field="startDate" header="Início" sortable style={{ minWidth: '8rem' }} />
                <Column field="endDate" header="Fim" sortable style={{ minWidth: '8rem' }} />
                <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                <Column header="Atividades" body={activitiesBodyTemplate} style={{ minWidth: '8rem' }} />
                <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '13rem' }} />
            </DataTable>

            <EventFormDialog
                visible={eventDialog}
                onHide={hideDialog}
                onSave={saveEvent}
                event={event}
                onChange={onChange}
                onActivitiesChange={onActivitiesChange}
                submitted={submitted}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                posterFile={posterFile}
                onPosterChange={setPosterFile}
                apiError={apiError}
            />

            <Dialog visible={deleteDialog} style={{ width: '450px' }}
                header="Confirmar Exclusão" modal footer={deleteFooter}
                onHide={() => setDeleteDialog(false)}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {deleteTarget && (
                        <span>Deseja excluir o evento <b>{deleteTarget.name}</b>?</span>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default EventList;
